/**
 * Micheals Infrastructure Advisory - STRATEGIC SERVICES REFINEMENT
 * VERSION: 6.1.0 (Governance & Fiduciary Advisory Interface)
 * FILE: src/pages/Services.js
 * ROLE: Integrated infrastructure governance and principal-level oversight.
 */

"use client";

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, X, Shield, Scan, Network, Activity, TrendingDown } from 'lucide-react';

// Components
import InteractiveBooking from '../components/InteractiveBooking';
import SEO from '../components/SEO';

// --- 1. DATA DEFINITION: GOVERNANCE PROTOCOLS ---
const GOVERNANCE_PROTOCOLS = [
  {
    id: 'Logic',
    navLabel: "Governance",
    title: "System Topology & Architecture",
    icon: Network,
    image: "/assets/portfolio/smart_controller.webp",
    description: "The architectural mapping of digital and physical backbones. We engineer the 'Unified Control Plane' for your asset, ensuring that all subsystems are logically aligned to eliminate technical debt and maximize long-term asset yield.",
    subCategories: [
      { label: "High-Capacity Data Backhauls", slug: "data-infra" },
      { label: "Unified Automation Topology", slug: "automation-logic" },
      { label: "Systemic Integration Strategy", slug: "integration-logic" },
      { label: "Critical Path Risk Modeling", slug: "path-logic" }
    ],
    callToAction: "Initialize Architecture Brief",
    metric: "Interoperability: 100%"
  },
  {
    id: 'Oversight',
    navLabel: "Stewardship",
    title: "Technical Stewardship",
    icon: Shield,
    image: "/assets/portfolio/smart-light.webp",
    description: "Independent fiduciary representation for the Asset Owner. We bridge the gap between architectural intent and implementation reality, providing non-biased verification to eliminate change orders, mitigate risk, and enforce absolute design fidelity.",
    subCategories: [
      { label: "Contractor Spec Enforcement", slug: "spec-enforcement" },
      { label: "Technical Tender Analysis", slug: "tender-review" },
      { label: "Milestone Integrity Verification", slug: "milestone-check" },
      { label: "Owner’s Representation (Remote)", slug: "remote-rep" }
    ],
    callToAction: "Access Stewardship Protocols",
    metric: "Liability Shield: 100%"
  },
  {
    id: 'Forensics',
    navLabel: "Forensics",
    title: "Diagnostic ROI Forensics",
    icon: Scan,
    image: "/assets/portfolio/blueprint.webp", 
    description: "Forensic-grade auditing for latent system vulnerabilities. Using advanced diagnostics and continuity analysis, we identify failure points before they manifest as operational or capital losses, protecting your infrastructure portfolio.",
    subCategories: [
      { label: "Thermal Continuity Auditing", slug: "thermal-audit" },
      { label: "System Integrity Analysis", slug: "integrity-analysis" },
      { label: "Predictive Failure Modeling", slug: "failure-report" },
      { label: "Infrastructure ROI Audits", slug: "roi-audit" }
    ],
    callToAction: "Initiate Forensic Briefing",
    metric: "Precision: Forensic Grade"
  }
];

// --- 2. SELECTION MODAL ---
const SelectionModal = ({ isOpen, onClose, protocol, onSelect }) => (
  <AnimatePresence mode="wait">
    {isOpen && (
      <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} className="absolute inset-0 bg-stone-950/95 backdrop-blur-md" 
        />
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative bg-white w-full max-w-lg p-8 rounded-sm shadow-2xl overflow-hidden"
        >
          <button onClick={onClose} className="absolute top-6 right-6 text-stone-400 hover:text-stone-950 transition-colors z-10">
            <X size={24}/>
          </button>
          
          <div className="space-y-8 relative z-10">
            <div className="space-y-1">
              <span className="text-amber-800 text-[10px] font-black uppercase tracking-[0.4em]">Engagement Protocol</span>
              <h3 className="text-4xl font-serif italic text-stone-950">{protocol?.title}</h3>
            </div>
            
            <div className="grid gap-3">
              {protocol?.subCategories.map((sub) => (
                <button 
                  key={sub.slug}
                  onClick={() => onSelect(sub)}
                  className="w-full text-left p-5 border border-stone-200 hover:border-amber-700 transition-all rounded-sm group flex justify-between items-center"
                >
                  <span className="text-stone-950 font-black text-[11px] uppercase tracking-widest">{sub.label}</span>
                  <ArrowRight className="text-stone-400 group-hover:text-amber-800" size={16} />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

// --- 3. MAIN SERVICES PAGE ---
const Services = ({ sessionId }) => {
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [activeProtocol, setActiveProtocol] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start start", "end start"] });
  const yHero = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scaleXProgress = useSpring(useScroll().scrollYProgress, { stiffness: 400, damping: 90 });

  const handleSelectionSubmit = (sub) => {
    setSelectedSub(sub);
    setIsSelectionOpen(false); 
    setTimeout(() => setIsBookingOpen(true), 450);
  };

  return (
    <div className="bg-white min-h-screen selection:bg-amber-100 overflow-x-hidden">
      <SEO title="Infrastructure Governance | Micheals Infrastructure Advisory" description="Strategic architecture, independent technical oversight, and forensic ROI auditing." />

      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-amber-800 origin-left z-[2000]" style={{ scaleX: scaleXProgress }} />

      <SelectionModal 
        isOpen={isSelectionOpen} 
        onClose={() => setIsSelectionOpen(false)} 
        protocol={activeProtocol}
        onSelect={handleSelectionSubmit}
      />

      <AnimatePresence>
        {isBookingOpen && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsBookingOpen(false)}
              className="absolute inset-0 bg-stone-950/95 backdrop-blur-xl"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 30 }}
              className="relative bg-white w-full max-w-lg overflow-hidden rounded-sm shadow-2xl"
            >
              <button onClick={() => setIsBookingOpen(false)} className="absolute top-6 right-6 text-stone-400 hover:text-stone-950 z-50"><X size={24}/></button>
              <div className="p-8">
                <InteractiveBooking initialService={selectedSub?.label} sessionId={sessionId} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <header ref={containerRef} className="relative w-full h-[80vh] flex flex-col items-center justify-center bg-stone-950 overflow-hidden">
        <motion.div style={{ y: yHero }} className="relative z-10 text-center px-6">
          <span className="text-amber-700 text-[10px] font-black uppercase tracking-[0.6em] mb-8 block">National Infrastructure Governance</span>
          <h1 className="text-6xl md:text-[8vw] font-serif text-white italic leading-none tracking-tighter">
            Strategic <span className="not-italic font-light text-stone-500">Stewardship.</span>
          </h1>
          <p className="max-w-3xl mx-auto mt-12 text-stone-400 text-lg font-light leading-relaxed">
            We leverage high-fidelity system architecture and rigorous oversight to ensure asset integrity. 
            Micheals Infrastructure Advisory serves as your technical authority, ensuring implementation aligns with both 
            design specifications and strategic capital targets.
          </p>
        </motion.div>
      </header>

      {/* Trust & Performance Bar */}
      <div className="bg-stone-50 border-y border-stone-200 py-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div className="space-y-2">
            <TrendingDown size={24} className="mx-auto text-amber-800" />
            <p className="text-stone-950 font-black uppercase tracking-widest text-[10px]">Asset Optimization</p>
          </div>
          <div className="space-y-2 border-x border-stone-200">
            <Shield size={24} className="mx-auto text-amber-800" />
            <p className="text-stone-950 font-black uppercase tracking-widest text-[10px]">Liability Shield</p>
          </div>
          <div className="space-y-2">
            <Activity size={24} className="mx-auto text-amber-800" />
            <p className="text-stone-950 font-black uppercase tracking-widest text-[10px]">Operational Continuity</p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 mb-40">
        {GOVERNANCE_PROTOCOLS.map((protocol, index) => (
          <section key={protocol.id} className="grid md:grid-cols-2 gap-20 lg:gap-32 py-40 border-b border-stone-100 last:border-0 items-center">
            <div className={`relative aspect-[4/5] rounded-sm overflow-hidden shadow-2xl bg-stone-100 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
              <img src={protocol.image} alt={protocol.title} className="w-full h-full object-cover grayscale transition-all duration-1000" />
              <div className="absolute bottom-8 left-8 right-8 bg-stone-950/90 p-6">
                 <div className="flex justify-between items-center">
                   <span className="text-white text-[9px] font-black tracking-widest uppercase">Performance KPI</span>
                   <span className="text-amber-500 text-[9px] font-black uppercase">{protocol.metric}</span>
                 </div>
              </div>
            </div>

            <div className={`space-y-10 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="text-amber-800"><protocol.icon size={24} /></div>
                  <span className="text-amber-800 text-[10px] font-black uppercase tracking-[0.4em]">Protocol 0{index + 1}</span>
                </div>
                <h2 className="text-5xl lg:text-7xl font-serif italic text-stone-950 leading-[0.9] tracking-tighter">
                  {protocol.title.split(' ')[0]} <br/>
                  <span className="not-italic font-light text-stone-400">{protocol.title.split(' ').slice(1).join(' ')}</span>
                </h2>
              </div>
              
              <p className="text-stone-600 text-lg font-light leading-relaxed max-w-lg">
                {protocol.description}
              </p>

              <button 
                onClick={() => { setActiveProtocol(protocol); setIsSelectionOpen(true); }}
                className="group flex items-center gap-6 bg-stone-950 text-white pl-8 pr-2 py-2 rounded-sm text-[10px] font-black uppercase tracking-[0.3em] hover:bg-amber-800 transition-all"
              >
                {protocol.callToAction}
                <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
                  <ArrowRight size={16} />
                </div>
              </button>
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

export default Services;